import {
  Badge,
  Button,
  Panel,
  PanelBody,
  ScrollArea,
  Separator,
  Skeleton,
  stepInstruction as getStepInstruction,
  getStepOverlayPoints,
  VideoPlayer,
} from "@autonoma/blacklight";
import { CaretRightIcon } from "@phosphor-icons/react/CaretRight";
import { FileTextIcon } from "@phosphor-icons/react/FileText";
import { LightbulbIcon } from "@phosphor-icons/react/Lightbulb";
import { StackIcon } from "@phosphor-icons/react/Stack";
import { WrenchIcon } from "@phosphor-icons/react/Wrench";
import { XCircleIcon } from "@phosphor-icons/react/XCircle";
import { createFileRoute } from "@tanstack/react-router";
import { DebugPanel } from "components/debug/debug-panel";
import { StepOutputDisplay } from "components/debug/step-output-display";
import { DetailRow } from "components/detail-row";
import { SentryLogsLink, TemporalLink } from "components/observability-links";
import { PlanMarkdown } from "components/plan-markdown";
import { type PullRequestRef, PullRequestDetailRows } from "components/pull-request-detail-rows";
import { NavigableLightbox, type NavigableStep, ScreenshotLightbox } from "components/screenshot-lightbox";
import { SystemFailurePanel, isSystemFailure } from "components/system-failure-panel";
import { useAuth } from "lib/auth";
import { formatDate } from "lib/format";
import { toGenerationBadgeVariant, toGenerationStatusLabel } from "lib/generation-status";
import { ensureGenerationDetailData, useGenerationDetail } from "lib/query/generations.queries";
import { useState } from "react";
import { AppLink } from "../../../-app-link";
import { useCurrentApplication } from "../../../-use-current-application";

export const Route = createFileRoute("/_blacklight/_app-shell/app/$appSlug/generations/$generationId/")({
  loader: async ({ context: { queryClient }, params: { generationId } }) => {
    await ensureGenerationDetailData(queryClient, generationId);
  },
  pendingComponent: GenerationDetailSkeleton,
  notFoundComponent: NotFoundGeneration,
  component: GenerationDetailPage,
});

type GenerationStatus = "pending" | "queued" | "running" | "success" | "failed";

interface GenerationStep {
  id: string;
  order: number;
  interaction: string;
  params: unknown;
  status: "success" | "failed";
  output?: object;
  error?: string;
  errorName?: string;
  screenshotBefore: string | null | undefined;
  screenshotAfter: string | null | undefined;
}

function stepDescription(step: GenerationStep): React.ReactNode {
  return getStepInstruction({ interaction: step.interaction, params: step.params });
}

function GenerationDetailPage() {
  const { generationId } = Route.useParams();
  const [showPlan, setShowPlan] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | undefined>(undefined);
  const currentApp = useCurrentApplication();
  const { isAdmin } = useAuth();
  const { data: generation } = useGenerationDetail(generationId);

  const status = generation.status as GenerationStatus;
  const isActive = status === "queued" || status === "running";
  const failure = generation.failure;

  const lightboxSteps: NavigableStep[] = generation.steps
    .filter((step: GenerationStep) => (step.screenshotBefore ?? step.screenshotAfter) != null)
    .map((step: GenerationStep) => {
      const src = step.screenshotBefore ?? step.screenshotAfter ?? "";
      const output = step.output as Record<string, unknown> | undefined;
      const points = output != null ? getStepOverlayPoints(output as Parameters<typeof getStepOverlayPoints>[0]) : [];
      return {
        src,
        alt: `Step ${step.order} - ${stepDescription(step)}`,
        points,
        stepNumber: step.order,
        description: stepDescription(step),
      };
    });

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="shrink-0 space-y-4 px-6 pt-6">
        <section className="flex items-center justify-end">
          <div className="hidden items-center gap-2 font-mono text-2xs text-text-tertiary sm:flex">
            <span>{generation.shortId}</span>
            <CaretRightIcon size={12} />
            <span>{currentApp.name}</span>
            <CaretRightIcon size={12} />
            <span>{formatDate(generation.createdAt)}</span>
          </div>
        </section>

        <div className="flex items-start justify-between gap-6 border border-border-dim bg-surface-raised p-5">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-medium tracking-tight text-text-primary">{generation.testPlan.name}</h1>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {isAdmin && generation.temporalWorkflow != null && (
              <>
                <TemporalLink
                  workflowId={generation.temporalWorkflow.workflowId}
                  runId={generation.temporalWorkflow.runId}
                />
                <SentryLogsLink filterField="testGenerationId" filterValue={generation.id} />
              </>
            )}
            <Button
              variant={showPlan ? "default" : "outline"}
              size="sm"
              onClick={() => setShowPlan(!showPlan)}
              aria-label="toggle-generation-plan"
            >
              <FileTextIcon size={14} />
              {showPlan ? "Close plan" : "View plan"}
            </Button>
            {isAdmin && (
              <Button
                variant={showDebug ? "default" : "outline"}
                size="sm"
                className="hidden lg:inline-flex"
                onClick={() => setShowDebug(!showDebug)}
                aria-label="toggle-debug-info"
              >
                <WrenchIcon size={14} />
                {showDebug ? "Close debug" : "Debug"}
              </Button>
            )}
            <Badge variant={toGenerationBadgeVariant(status)} className="h-7 px-3 text-xs">
              {toGenerationStatusLabel(status)}
            </Badge>
          </div>
        </div>
      </div>

      {/* Content area: sidebar + steps + plan */}
      <div className="flex min-h-0 flex-1 gap-6 overflow-hidden px-6 py-5 pb-6">
        {/* Sidebar */}
        {!showPlan && (
          <div className="hidden w-72 shrink-0 overflow-y-auto lg:block">
            <GenerationDetailSidebar
              status={status}
              createdAt={generation.createdAt}
              stepCount={generation.steps.length}
              reasoning={generation.reasoning ?? undefined}
              failure={failure ?? undefined}
              reasoningScreenshot={generation.finalScreenshot ?? undefined}
              videoUrl={generation.videoUrl ?? undefined}
              optimizedVideoUrl={generation.optimizedVideoUrl ?? undefined}
              test={generation.testCase ?? undefined}
              pullRequest={generation.pullRequest}
            />
          </div>
        )}

        {/* Steps */}
        <div className={`flex min-w-0 flex-1 flex-col gap-4 overflow-y-auto ${showDebug ? "lg:max-w-2xl" : ""}`}>
          {isSystemFailure(failure) && <SystemFailurePanel failure={failure} />}
          {generation.steps.length > 0 ? (
            <div>
              {generation.steps.map((step: GenerationStep, i: number) => {
                const hasScreenshot = (step.screenshotBefore ?? step.screenshotAfter) != null;
                const lightboxIdx = hasScreenshot ? lightboxSteps.findIndex((ls) => ls.stepNumber === step.order) : -1;
                return (
                  <StepCard
                    key={step.id}
                    step={step}
                    isLast={i === generation.steps.length - 1}
                    onImageClick={lightboxIdx >= 0 ? () => setLightboxIndex(lightboxIdx) : undefined}
                  />
                );
              })}
            </div>
          ) : (
            !isSystemFailure(failure) && (
              <NoStepsEvidence
                isActive={isActive}
                finalScreenshot={generation.finalScreenshot ?? undefined}
                conversationUrl={generation.conversationUrl ?? undefined}
                isAdmin={isAdmin}
              />
            )
          )}
        </div>

        {/* Debug panel */}
        {isAdmin && showDebug && generation.debug != null && (
          <div className="hidden w-80 shrink-0 overflow-y-auto lg:block">
            <DebugPanel debug={generation.debug} conversationUrl={generation.conversationUrl} />
          </div>
        )}

        {/* Plan panel */}
        {showPlan && (
          <div className="hidden w-1/2 shrink-0 overflow-hidden lg:block">
            <PlanPanel content={generation.testPlan.plan} />
          </div>
        )}
      </div>

      <NavigableLightbox
        steps={lightboxSteps}
        activeIndex={lightboxIndex}
        onClose={() => setLightboxIndex(undefined)}
        onNavigate={setLightboxIndex}
      />
    </div>
  );
}

// --- Sidebar ---

// The agent ran to completion but did not pass. These verdicts keep their prose in
// `reasoning` and render under the lightbulb box (not the critical SystemFailurePanel);
// the sub-label just tells the two apart. System failures return undefined here.
function agentVerdictLabel(failure: { kind: string } | undefined): string | undefined {
  if (failure == null) return undefined;
  if (failure.kind === "agent_failed") return "Agent could not complete the test";
  if (failure.kind === "max_steps") return "Ran out of steps";
  return undefined;
}

interface GenerationDetailSidebarProps {
  status: GenerationStatus;
  createdAt: Date;
  stepCount: number;
  reasoning?: string;
  failure?: { kind: string };
  reasoningScreenshot?: string;
  videoUrl?: string;
  optimizedVideoUrl?: string;
  test?: { id: string; name: string; slug: string };
  pullRequest?: PullRequestRef;
}

function GenerationDetailSidebar({
  status,
  createdAt,
  stepCount,
  reasoning,
  failure,
  reasoningScreenshot,
  videoUrl,
  optimizedVideoUrl,
  test,
  pullRequest,
}: GenerationDetailSidebarProps) {
  const verdictLabel = agentVerdictLabel(failure);
  return (
    <div className="flex flex-col gap-4">
      <Panel>
        <PanelBody className="flex flex-col gap-5 p-5">
          <DetailRow label="Status">
            <Badge variant={toGenerationBadgeVariant(status)} className="mt-1">
              {toGenerationStatusLabel(status)}
            </Badge>
          </DetailRow>

          <DetailRow label="Created">
            <span>{formatDate(createdAt)}</span>
          </DetailRow>

          <DetailRow label="Steps">
            <span className="font-medium">{stepCount}</span>
            <span className="ml-1 text-text-tertiary">{stepCount === 1 ? "step" : "steps"}</span>
          </DetailRow>

          {test != null && (
            <>
              <Separator />
              <DetailRow label="Generated Test">
                <AppLink
                  to="/app/$appSlug/tests/$testSlug"
                  params={{ testSlug: test.slug }}
                  className="inline-flex items-center gap-1 font-medium text-primary-ink hover:underline"
                >
                  {test.name}
                </AppLink>
              </DetailRow>
            </>
          )}

          {pullRequest != null && (
            <>
              <Separator />
              <PullRequestDetailRows pullRequest={pullRequest} />
            </>
          )}
        </PanelBody>
      </Panel>

      {reasoning != null && (
        <div className="border border-status-warn/30 bg-status-warn/5 p-5">
          <div className="mb-3 flex items-center gap-2">
            <LightbulbIcon size={14} className="text-status-warn" />
            <span className="font-mono text-2xs font-semibold uppercase tracking-widest text-status-warn">
              Reasoning
            </span>
          </div>
          {verdictLabel != null && <p className="mb-3 text-xs font-medium text-text-secondary">{verdictLabel}</p>}
          {reasoningScreenshot != null && (
            <div className="mb-3">
              <ScreenshotLightbox
                src={reasoningScreenshot}
                alt="Last step screenshot"
                className="w-full border border-status-warn/20"
              />
            </div>
          )}
          <p className="text-sm leading-relaxed text-text-primary">{reasoning}</p>
        </div>
      )}

      {videoUrl != null && (
        <Panel>
          <PanelBody className="p-5">
            <VideoPlayer src={videoUrl} optimizedSrc={optimizedVideoUrl} label="Recording" />
          </PanelBody>
        </Panel>
      )}
    </div>
  );
}

// --- Step Card ---

function StepCard({
  step,
  isLast,
  onImageClick,
}: {
  step: GenerationStep;
  isLast: boolean;
  onImageClick?: () => void;
}) {
  const screenshot = step.screenshotBefore ?? step.screenshotAfter;
  const instruction = stepDescription(step);
  const isFailed = step.status === "failed";

  return (
    <div className="flex gap-4">
      <div className="mt-0.5 flex flex-col items-center">
        <div
          className={`flex size-6 shrink-0 items-center justify-center border bg-surface-base ${
            isFailed ? "border-status-critical/50" : "border-border-dim"
          }`}
        >
          {isFailed ? (
            <XCircleIcon size={14} weight="fill" className="text-status-critical" />
          ) : (
            <span className="font-mono text-3xs text-text-tertiary">{step.order}</span>
          )}
        </div>
        {!isLast && <div className="mt-1 h-full w-px bg-border-dim" />}
      </div>

      <div
        className={`mb-3 flex-1 overflow-hidden border bg-surface-raised ${
          isFailed ? "border-status-critical/40" : "border-border-dim"
        }`}
      >
        <div className="flex">
          {screenshot != null && (
            <div
              role="button"
              className="relative aspect-video w-52 shrink-0 self-start overflow-hidden border-r border-border-dim bg-surface-base cursor-zoom-in"
              onClick={onImageClick}
            >
              <img
                src={screenshot}
                alt={`Step ${step.order}`}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
          )}
          <div className="flex min-w-0 flex-1 flex-col justify-center gap-2 px-4 py-3">
            <div className="flex items-start justify-between gap-2">
              <p className="min-w-0 break-words text-sm font-medium leading-snug text-text-primary">{instruction}</p>
              <span className="shrink-0 font-mono text-2xs font-medium text-text-tertiary">#{step.order}</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge variant="outline" className="w-fit font-mono text-3xs uppercase">
                {step.interaction}
              </Badge>
              {isFailed && (
                <Badge variant="critical" className="w-fit gap-1 font-mono text-3xs uppercase">
                  <XCircleIcon size={10} weight="fill" />
                  {step.errorName ?? "Failed"}
                </Badge>
              )}
            </div>
            {isFailed && step.error != null && (
              <div className="border-l-2 border-status-critical/50 bg-status-critical/5 px-2.5 py-1.5">
                <p className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed text-status-critical">
                  {step.error}
                </p>
              </div>
            )}
          </div>
        </div>
        {/* Long output spans full width below the header. */}
        {step.output != null && typeof step.output === "object" && Object.keys(step.output).length > 0 && (
          <div className="min-w-0 px-4 pb-3">
            <StepOutputDisplay output={step.output as Record<string, unknown>} />
          </div>
        )}
      </div>
    </div>
  );
}

// Empty steps state: while active, steps are still coming; on a terminal failure, show the final
// screenshot; otherwise show a "nothing persisted" message with an admin link to the conversation.
function NoStepsEvidence({
  isActive,
  finalScreenshot,
  conversationUrl,
  isAdmin,
}: {
  isActive: boolean;
  finalScreenshot?: string;
  conversationUrl?: string;
  isAdmin: boolean;
}) {
  if (isActive) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 border border-dashed border-border-dim py-14 text-center">
        <StackIcon size={24} className="text-text-secondary" />
        <p className="text-sm text-text-secondary">Steps will appear here as the AI generates them...</p>
      </div>
    );
  }

  if (finalScreenshot != null) {
    return (
      <div className="flex flex-col gap-3 border border-dashed border-border-dim p-5">
        <p className="text-sm text-text-secondary">
          No browser steps were persisted. Here is the final state when the run stopped - see the failure reason for
          what went wrong.
        </p>
        <ScreenshotLightbox
          src={finalScreenshot}
          alt="Final screenshot when the run stopped"
          className="max-h-[480px] w-full border border-border-dim object-contain"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center gap-2 border border-dashed border-border-dim py-14 text-center">
      <StackIcon size={24} className="text-text-secondary" />
      <p className="text-sm text-text-secondary">No browser steps were persisted for this generation.</p>
      {isAdmin && conversationUrl != null && (
        <a
          href={conversationUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-2xs uppercase tracking-widest text-primary-ink hover:underline"
        >
          View agent conversation
        </a>
      )}
    </div>
  );
}

// --- Plan Panel ---

function PlanPanel({ content }: { content: string }) {
  return (
    <div className="flex h-full flex-col overflow-hidden border border-border-dim">
      <div className="flex shrink-0 items-center gap-2 border-b border-border-dim bg-surface-base px-4 py-3">
        <FileTextIcon size={14} className="text-text-tertiary" />
        <span className="font-mono text-2xs font-medium uppercase tracking-wider text-text-tertiary">Test Plan</span>
      </div>
      <ScrollArea className="flex-1 bg-surface-raised">
        <div className="p-5">
          <PlanMarkdown content={content} />
        </div>
      </ScrollArea>
    </div>
  );
}

// --- Skeleton ---

function GenerationDetailSkeleton() {
  return (
    <div className="flex-1 p-6">
      <Skeleton className="mb-6 h-4 w-32" />
      <div className="mb-8">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-8 w-72" />
            <Skeleton className="h-4 w-full max-w-lg" />
          </div>
          <Skeleton className="h-7 w-24" />
        </div>
      </div>
      <div className="flex gap-6">
        <div className="w-72 shrink-0">
          <div className="border border-border-dim bg-surface-raised p-5">
            <div className="flex flex-col gap-5">
              {["sk-1", "sk-2", "sk-3"].map((id) => (
                <div key={id} className="flex flex-col gap-1.5">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-5 w-32" />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-3">
          {["step-1", "step-2", "step-3", "step-4"].map((id, i) => (
            <div key={id} className="flex gap-3">
              <div className="flex flex-col items-center">
                <Skeleton className="size-6" />
                {i < 3 && <div className="mt-1 h-20 w-px bg-border-dim" />}
              </div>
              <Skeleton className="mb-3 h-24 flex-1" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function NotFoundGeneration() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
      <p className="text-sm font-medium text-text-primary">Generation not found</p>
      <p className="text-sm text-text-secondary">This generation may have been deleted.</p>
      <AppLink
        to="/app/$appSlug/pull-requests"
        className="mt-2 font-mono text-2xs font-semibold uppercase tracking-widest text-text-primary transition-colors hover:underline"
      >
        Back to pull requests
      </AppLink>
    </div>
  );
}
